import { postTts1, postTts3 } from '@/api/englishLearning/public'
import { COURSE_TTS_API, canCustomizeTtsSpeed } from '@/config/ttsApiSwitch'
import { DEFAULT_TTS_VOICE } from '@/config/ttsVoices'
import { resolveTtsOssUrl } from '@/utils/ttsOssResolve'

export { COURSE_TTS_API, canCustomizeTtsSpeed }

/** 无发音源切换；设置持久化用 */
export const TTS_PROVIDER = 'cibei'

export function normalizeTtsProvider () {
  return TTS_PROVIDER
}

/** voice_type → lang */
export function ttsLangFromVoice (voiceType) {
  const v = String(voiceType || '').toLowerCase()
  if (v.startsWith('af_') || v.startsWith('am_') || v.startsWith('en-us')) return 'en-US'
  if (v.startsWith('bf_') || v.startsWith('bm_') || v.startsWith('en-gb')) return 'en-GB'
  return 'en-US'
}

const USE_TTS3 = COURSE_TTS_API === 'tts3'

// ========== tts1 语速（Demo：0.70～1.30，正常=1）保留 ==========
// export const TTS_UI_SPEED_MIN = 0.7
// export const TTS_UI_SPEED_MAX = 1.3
// export const TTS_UI_SPEED_STEP = 0.05
// export const DEFAULT_TTS_UI_SPEED = 1
const TTS1_SPEED = { min: 0.7, max: 1.3, step: 0.05, default: 1, sliderScale: 20 }

// ========== tts3 语速（Edge rate %：-30～+20，默认=-10）==========
const TTS3_SPEED = { min: -30, max: 20, step: 5, default: -10, sliderScale: 1 }

export const TTS_UI_SPEED_MIN = USE_TTS3 ? TTS3_SPEED.min : TTS1_SPEED.min
export const TTS_UI_SPEED_MAX = USE_TTS3 ? TTS3_SPEED.max : TTS1_SPEED.max
export const TTS_UI_SPEED_STEP = USE_TTS3 ? TTS3_SPEED.step : TTS1_SPEED.step
export const DEFAULT_TTS_UI_SPEED = USE_TTS3 ? TTS3_SPEED.default : TTS1_SPEED.default
/** 音量等仍用 ×20；tts3 语速不走此 scale */
export const TTS_UI_SLIDER_SCALE = TTS1_SPEED.sliderScale

function currentUserId () {
  try {
    const raw = localStorage.getItem('userInfo')
    const u = raw ? JSON.parse(raw) : null
    return u?.id ?? u?.userId ?? u?.user_id ?? ''
  } catch (_) {
    return ''
  }
}

/** 旧 tts1 UI 刻度（0.7～1.3）→ Edge 百分比 */
function migrateTts1UiSpeedToEdgePercent (n) {
  if (n < 0.7 || n > 1.3) return n
  return ((n - 0.7) / 0.6) * (TTS3_SPEED.max - TTS3_SPEED.min) + TTS3_SPEED.min
}

export function normalizeUiSpeed (v) {
  let n = Number(v)
  if (!Number.isFinite(n)) return DEFAULT_TTS_UI_SPEED
  if (USE_TTS3) {
    n = migrateTts1UiSpeedToEdgePercent(n)
    const clamped = Math.min(TTS_UI_SPEED_MAX, Math.max(TTS_UI_SPEED_MIN, n))
    return Math.round(clamped / TTS_UI_SPEED_STEP) * TTS_UI_SPEED_STEP
  }
  const clamped = Math.min(TTS_UI_SPEED_MAX, Math.max(TTS_UI_SPEED_MIN, n))
  return Math.round(clamped * TTS1_SPEED.sliderScale) / TTS1_SPEED.sliderScale
}

/**
 * 实际生效语速（全员可自定义；非法值回落到 DEFAULT_TTS_UI_SPEED）
 * @param {*} uiSpeed
 * @param {*} [_userId] 历史参数，已忽略
 */
export function resolveEffectiveUiSpeed (uiSpeed, _userId) {
  return normalizeUiSpeed(uiSpeed)
}

/**
 * UI 语速 → 出站 speed_ratio
 * - tts1：number（0.70～1.30）
 * - tts3：string（"-30" / "-10" / "10"…，无 %；后端拼接后直传 Edge）
 */
export function mapUiSpeedToSpeedRatio (uiSpeed, userId) {
  const s = resolveEffectiveUiSpeed(uiSpeed, userId)
  if (USE_TTS3) return String(s)
  return s
}

/** 设置页展示：tts3 用 +0% / -10%；tts1 用 1.00x */
export function formatUiSpeedLabel (uiSpeed) {
  const s = normalizeUiSpeed(uiSpeed)
  if (!USE_TTS3) return `${Number(s).toFixed(2)}x`
  if (s > 0) return `+${s}%`
  return `${s}%`
}

/**
 * @deprecated 新模型不支持音量；tts1 不再传 loudness_ratio。
 * 旧映射：UI 0～2（1 正常）→ loudness_ratio 0～1（0.5 正常）
 */
export function mapUiVolumeToLoudnessRatio (uiVolume) {
  const n = Number(uiVolume)
  const v = Number.isFinite(n) ? n : 1
  return Math.min(1, Math.max(0, v / 2))
}

/** 出站 phoneme 清洗（tts1 / WS 共用） */
export function prepareTtsPhoneme (phoneme) {
  // 旧规则（仅去斜杠）保留备用：
  // return String(phoneme || '')
  //   .replace(/\//g, '')
  //   .trim()

  // 新规则：直接传对应音标；`/|/` 与 `,` → 空格；再去掉残留的 `|` `/`
  return String(phoneme || '')
    .replace(/\/\|\//g, ' ')
    .replace(/,/g, ' ')
    .replace(/[|/]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * tts1 是否附带 phoneme：
 * - en 字符长度 ≤ 1（或仅 phoneme）：可传
 * - en 长度 > 1：默认不传
 * - 例外：缩写（isAbbreviation，来自 tokens.tags=abbreviation）、分块拼读（segmentPhoneme）、显式 forcePhoneme
 */
export function shouldAttachTts1Phoneme (en, {
  isAbbreviation = false,
  segmentPhoneme = false,
  forcePhoneme = false
} = {}) {
  if (forcePhoneme || segmentPhoneme || isAbbreviation) return true
  const text = String(en || '').trim()
  if (!text) return true
  return text.length <= 1
}

/**
 * 清洗并按出站规则决定最终 phoneme（空串表示不传）
 */
export function resolveOutboundTtsPhoneme (en, phoneme, options = {}) {
  const phonemeVal = prepareTtsPhoneme(phoneme)
  if (!phonemeVal) return ''
  return shouldAttachTts1Phoneme(en, options) ? phonemeVal : ''
}

/**
 * 统一 TTS：POST /course/course/tts1 | tts3（由 COURSE_TTS_API 开关）
 * tts3：先预取 OSS（默认语速 `sound/{Voice}/{en}.mp3`，其它语速 `sound/{Voice}/{speed.00}/{en}.mp3`），未命中再调接口
 * @param {string} text
 * @param {{ voice_type?: string, speed?: number, volume?: number, phoneme?: string, isAbbreviation?: boolean, segmentPhoneme?: boolean, forcePhoneme?: boolean, skipOssPrefetch?: boolean }} options
 *        skipOssPrefetch：为 true 时跳过 OSS（OSS 播放失败回落 tts3 时使用）
 *        speed：tts1 为 Demo 刻度；tts3 为 Edge 百分比（非白名单固定 -10）
 *        phoneme：整词 tts3 默认不传；自然拼读/拆分发音（segmentPhoneme）或 forcePhoneme 时透传
 *        segmentPhoneme：音素/拆分项——禁止走 OSS 整词缓存（OSS 是 sound/{Voice}/ 完整词句，
 *        与站点 /sound/letter|blend|… 字素包不是同一套）；无字素 CDN 命中时由上层再调本函数走 tts3+phoneme
 */
export async function requestTts (text, {
  voice_type: voiceType,
  speed = DEFAULT_TTS_UI_SPEED,
  volume: _volume = 1,
  phoneme = '',
  isAbbreviation = false,
  segmentPhoneme = false,
  forcePhoneme = false,
  skipOssPrefetch = false
} = {}) {
  const useTts3 = COURSE_TTS_API === 'tts3'
  const en = String(text || '').trim()
  // tts3 整词仍不传 phoneme；grapheme/syllable（segmentPhoneme）与 forcePhoneme 须透传
  const phonemeVal = (!useTts3 || segmentPhoneme || forcePhoneme)
    ? resolveOutboundTtsPhoneme(en, phoneme, {
      isAbbreviation,
      segmentPhoneme,
      forcePhoneme
    })
    : ''
  if (!en && !phonemeVal) {
    return Promise.reject(new Error('TTS 文本为空'))
  }

  const vt = String(voiceType || DEFAULT_TTS_VOICE).trim() || DEFAULT_TTS_VOICE
  const enOut = en || phonemeVal
  const speed_ratio = mapUiSpeedToSpeedRatio(speed, currentUserId())
  const payload = {
    en: enOut,
    lang: ttsLangFromVoice(vt),
    voice_type: vt,
    speed_ratio
    // 新模型不支持音量，不传 loudness_ratio（微软兜底由后端处理）
    // loudness_ratio: mapUiVolumeToLoudnessRatio(volume),
  }
  if (phonemeVal) {
    payload.phoneme = phonemeVal
  }

  // 仅完整单词/句子可预取 OSS；音素与拆分项绝不走 OSS
  if (useTts3 && en && !skipOssPrefetch && !segmentPhoneme) {
    const ossUrl = await resolveTtsOssUrl(en, vt, speed_ratio)
    if (ossUrl) return { code: 1, msg: 'ok', data: { voice: ossUrl } }
  }

  return useTts3 ? postTts3(payload) : postTts1(payload)
}
